import { memo, useCallback, useState } from 'react'
import { Popover } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { BetaPrompt, type BetaPromptAlign, type BetaPromptSide } from './nested/beta-prompt'
import { useOpenFeedback } from '@renderer/components/feedback/use-open-feedback'
import { isDev } from '@shared/utils/platform'

/**
 * Spelled out rather than rendered as a `Badge`, which is a plain function
 * component. On React 18 the ref the positioner anchors to would go nowhere.
 * The chip reads as a label at 20px tall, so a pseudo-element expands the hit
 * target back to 24px.
 */
const BADGE_CLASS = [
  'bg-foreground/[6%] text-foreground-secondary relative h-5 shrink-0 rounded-md border px-1.5',
  'text-[10px] font-semibold uppercase leading-none tracking-wider',
  'hover:bg-element-hover hover:text-foreground cursor-pointer select-none transition-colors',
  'after:absolute after:-inset-x-1 after:-inset-y-[3px] after:content-[""]',
].join(' ')

interface BetaFeedbackProps {
  /** Where the feedback form was opened from, for analytics. */
  source: string
  className?: string
  badgeClassName?: string
  side?: BetaPromptSide
  align?: BetaPromptAlign
  /** Replaces opening this window's support widget, for surfaces where it can't take clicks. */
  openFeedback?: () => void
}

/**
 * A `BETA` chip that opens a popup saying the app is still rough, with a way
 * into the feedback form. Only ever opens from the chip — never unprompted.
 */
const BetaFeedbackRoot = memo(
  ({ source, className, badgeClassName, side = 'top', align = 'center', openFeedback }: BetaFeedbackProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const openFeedbackHere = useOpenFeedback(source)
    const sendFeedback = openFeedback ?? openFeedbackHere

    const onSendFeedback = useCallback(() => {
      setIsOpen(false)
      sendFeedback()
    }, [sendFeedback])

    return (
      <div className={twMerge('flex items-center', className)}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger aria-label="Highlight for Mac is in beta" className={twMerge(BADGE_CLASS, badgeClassName)}>
            {isDev ? 'Dev' : 'Beta'}
          </Popover.Trigger>
          <BetaPrompt side={side} align={align} onSendFeedback={onSendFeedback} />
        </Popover>
      </div>
    )
  },
)
BetaFeedbackRoot.displayName = 'BetaFeedback'

export const BetaFeedback = Object.assign(BetaFeedbackRoot, {
  Prompt: BetaPrompt,
})

export type { BetaPromptAlign, BetaPromptSide }
